import React, { useMemo } from "react";
import LBox, { BackgroundColorProp } from "@components/LBox";
import { BackgroundColor } from "@theme";

interface DecagonProps extends BackgroundColorProp {
  children: React.ReactNode;
  size?: number;
  showBackground?: boolean;
  borderWidth?: number;
  borderColor?: BackgroundColor | BackgroundColor[];
}

// Use the same SVG path calculation as the React Native version
const calculatePath = (size: number) => {
  const originalWidth = 84.0408 - 15.5647;
  const originalHeight = 73.3976 - 2.60243;
  const originalCenterX = (84.0408 + 15.5647) / 2;
  const originalCenterY = (73.3976 + 2.60243) / 2;

  // Scale to fit the desired size (use the larger dimension to ensure it fills)
  const maxOriginalDimension = Math.max(originalWidth, originalHeight);
  const scale = size / maxOriginalDimension;

  // Calculate offset to center the scaled shape
  const targetCenter = size / 2;
  const offsetX = targetCenter - originalCenterX * scale;
  const offsetY = targetCenter - originalCenterY * scale;

  const scaledPath = `M${47.9486 * scale + offsetX} ${2.60243 * scale + offsetY}C${49.1537 * scale + offsetX} ${2.21089 * scale + offsetY} ${50.4518 * scale + offsetX} ${2.21089 * scale + offsetY} ${51.6568 * scale + offsetX} ${2.60243 * scale + offsetY}L${69.1089 * scale + offsetX} ${8.27295 * scale + offsetY}C${70.314 * scale + offsetX} ${8.6645 * scale + offsetY} ${71.3641 * scale + offsetX} ${9.4275 * scale + offsetY} ${72.1089 * scale + offsetX} ${10.4526 * scale + offsetY}L${82.8949 * scale + offsetX} ${25.2982 * scale + offsetY}C${83.6396 * scale + offsetX} ${26.3233 * scale + offsetY} ${84.0408 * scale + offsetX} ${27.5578 * scale + offsetY} ${84.0408 * scale + offsetX} ${28.8249 * scale + offsetY}V${47.1751 * scale + offsetY}C${84.0408 * scale + offsetX} ${48.4422 * scale + offsetY} ${83.6396 * scale + offsetX} ${49.6767 * scale + offsetY} ${82.8949 * scale + offsetX} ${50.7018 * scale + offsetY}L${72.1089 * scale + offsetX} ${65.5474 * scale + offsetY}C${71.3641 * scale + offsetX} ${66.5725 * scale + offsetY} ${70.314 * scale + offsetX} ${67.3355 * scale + offsetY} ${69.1089 * scale + offsetX} ${67.727 * scale + offsetY}L${51.6568 * scale + offsetX} ${73.3976 * scale + offsetY}C${50.4518 * scale + offsetX} ${73.7891 * scale + offsetY} ${49.1537 * scale + offsetX} ${73.7891 * scale + offsetY} ${47.9486 * scale + offsetX} ${73.3976 * scale + offsetY}L${30.4966 * scale + offsetX} ${67.727 * scale + offsetY}C${29.2915 * scale + offsetX} ${67.3355 * scale + offsetY} ${28.2413 * scale + offsetX} ${66.5725 * scale + offsetY} ${27.4966 * scale + offsetX} ${65.5474 * scale + offsetY}L${16.7106 * scale + offsetX} ${50.7018 * scale + offsetY}C${15.9658 * scale + offsetX} ${49.6767 * scale + offsetY} ${15.5647 * scale + offsetX} ${48.4422 * scale + offsetY} ${15.5647 * scale + offsetX} ${47.1751 * scale + offsetY}V${28.8249 * scale + offsetY}C${15.5647 * scale + offsetX} ${27.5578 * scale + offsetY} ${15.9658 * scale + offsetX} ${26.3233 * scale + offsetY} ${16.7106 * scale + offsetX} ${25.2982 * scale + offsetY}L${27.4966 * scale + offsetX} ${10.4526 * scale + offsetY}C${28.2413 * scale + offsetX} ${9.4275 * scale + offsetY} ${29.2915 * scale + offsetX} ${8.6645 * scale + offsetY} ${30.4966 * scale + offsetX} ${8.27295 * scale + offsetY}L${47.9486 * scale + offsetX} ${2.60243 * scale + offsetY}Z`;

  return scaledPath;
};

const Decagon: React.FC<DecagonProps> = ({
  children,
  size = 72,
  borderWidth = 0,
  borderColor,
}) => {
  const svgPath = useMemo(() => calculatePath(size), [size]);

  return (
    <LBox
      width={size}
      height={size}
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      {borderWidth > 0 && borderColor && (
        <LBox
          width={size}
          height={size}
          position="absolute"
          backgroundColor={borderColor}
          style={
            {
              clipPath: `path('${svgPath}')`,
              transform: "scale(0.83)",
            } as any
          }
        />
      )}

      {/* Content container with decagon mask */}
      <LBox
        width={size}
        height={size}
        alignItems="center"
        justifyContent="center"
        style={
          {
            clipPath: `path('${svgPath}')`,
            transform: "scale(0.73)", // Match the scale from original component
            transformOrigin: "center",
          } as any
        }
      >
        <LBox style={{ transform: "scale(1.01)" }}>{children}</LBox>
      </LBox>
    </LBox>
  );
};

export default Decagon;
